import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { useCallback, useEffect, useState } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, { RowWithOptionalClick } from "../../../table";
import React from "react";
import AddUserModal, { Inputs, PERMISSION_OPTIONS } from "./addAdminModal";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import {
  createClientAdmin,
  editClientAdminPermissions,
  getClientAdmins,
} from "../../../../../setup/api/services/user";
import SuccessModal from "./SuccessModal";
import { AuthConfig } from "../../../../../setup/context/config/authConfig";
import Pagination from "../../../pagination";

interface UsersInformationProps {
  organization: IOrganizations | undefined;
}

// Define tier information structure
interface UserInfo extends RowWithOptionalClick {
  name: string;
  phoneNumber: string;
  email: string;
  state: string | undefined;
  permissions: string[];
  orgOwner: boolean;
  id: number;
}

const UsersInformation = ({ organization }: UsersInformationProps) => {
  const { t } = useTranslation();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const pageSize = 10;

  // Define table columns
  const columns = [
    { type: "name", value: t("lbl_username") },
    { type: "phoneNumber", value: t("mobile_number") },
    { type: "email", value: t("email") },
    {
      type: "permissions",
      value: t("lbl_permissions"),
    },
    { type: "state", value: "" },
    { type: "actions", value: "" },
  ];

  const fetchOrganizationAdmins = useCallback(async () => {
    if (!organization) return;
    setIsLoading(true);
    try {
      const response = await getClientAdmins(page - 1, pageSize);

      setUsers(
        response?.data?.content.map((clientAdmin) => ({
          id: +clientAdmin?.userIdentification,
          name: clientAdmin?.profile?.fullName,
          phoneNumber: clientAdmin?.phoneNumber,
          email: clientAdmin?.email || "",
          state: clientAdmin?.profile?.verified ? "Active" : "Inactive",
          permissions: clientAdmin?.authorities,
          orgOwner: clientAdmin?.orgOwner,
        }))
      );

      setPagesCount(response.data.totalPages);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [organization, page]);

  useEffect(() => {
    fetchOrganizationAdmins();
  }, [organization, t, fetchOrganizationAdmins, page]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  const handleEdit = (index) => {
    setMode("edit");
    setSelectedIndex(index);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  // Render cell based on column type and data
  const renderCell = (columnType: string, row: UserInfo) => {
    switch (columnType) {
      case "name":
        return <span className={styles.nameValue}>{row.name}</span>;
      case "phoneNumber":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row?.phoneNumber}
          </span>
        );
      case "email":
        return <span className={styles.nameValue}>{row?.email}</span>;
      case "permissions":
        return (
          <span className={styles.nameValue}>
            {row?.permissions
              ?.filter((permission) =>
                PERMISSION_OPTIONS.some((option) => option.id === permission)
              )
              ?.map(
                (permission) =>
                  PERMISSION_OPTIONS.find((option) => option.id === permission)
                    ?.label
              )
              .join(", ")}
          </span>
        );
      case "state":
        return (
          <span className={styles.statusContainer}>
            {/* {!row?.orgOwner && <IosSwitch rtl status="off" />} */}
          </span>
        );
      case "actions":
        return (
          <div className={styles.actionsContainer}>
            {!row?.orgOwner && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={handleEdit?.bind(
                  null,
                  users?.findIndex((user) => user?.id === row?.id)
                )}>
                <img
                  src="/assets/images/svg/edit.svg"
                  alt="edit"
                  style={{ width: "17px", height: "17px" }}
                />
              </button>
            )}

            {/* {!row?.orgOwner && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  setSelectedIndex(
                    users?.findIndex((user) => user?.id === row?.id)
                  );
                }}>
                <img
                  src="/assets/images/svg/deleteAlt.svg"
                  alt="delete"
                  style={{ width: "17px", height: "17px" }}
                />
              </button>
            )} */}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
        }}>
        <div className={styles.headerContainer}>
          <h3 className={styles.title}>إدارة المستخدمين</h3>
          <p className={styles.subtitle}>
            يمكنك إضافة مستخدمين جدد أو تعديل صلاحيات المستخدمين الحاليين
          </p>
        </div>
        <div className={styles.addButton}>
          {(authorities?.includes("CLIENT_ADMIN_CREATE_USER") ||
            authorities?.includes("CLIENT_ADMIN") ||
            authorities?.includes("ORGANIZATION_OWNER")) && (
            <PrimaryButton
              onClick={() => {
                setMode("add");
                setModalOpen(true);
              }}>
              {`+ ${t("lbl_add_user")}`}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <UsersBusinessTable
          columns={columns}
          rows={users}
          renderCell={(columnType, row) =>
            renderCell(columnType, row as UserInfo)
          }
          pagesCount={pagesCount}
          page={page}
          pageSize={pageSize}
          handlePaginationChange={handlePaginationChange}
          totalCount={totalCount}
        />
      </div>

      <SuccessModal
        modalOpen={successModalOpen}
        handleClose={() => {
          setSuccessModalOpen(false);
          fetchOrganizationAdmins();
        }}
        email={credentials?.email}
        password={credentials?.password}
      />

      <AddUserModal
        loading={loading}
        modalOpen={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          if (mode === "edit") fetchOrganizationAdmins();
        }}
        handleSave={async (data: Inputs) => {
          let result;
          setLoading(true);
          const [first, ...rest] = data.name.split(" ");
          try {
            if (mode === "add") {
              result = await createClientAdmin({
                email: data?.email,
                authorities: [...data.permissions],
                clientUserId: data?.phoneNumber,
                phoneNumber: data?.phoneNumber,
                firstName: first,
                lastName: rest?.join(" "),
              });
              setCredentials({
                email: result?.data?.email,
                password: result?.data?.clientUserToken,
              });
              setModalOpen(false);
              setSuccessModalOpen(true);
            } else {
              // TODO: permissions only
              result = await editClientAdminPermissions(
                users?.[selectedIndex]?.id,
                {
                  roles: [...data.permissions],
                }
              );
              setModalOpen(false);
            }
          } catch (error) {
            console.error(error);
          } finally {
            setLoading(false);
          }
        }}
        {...(mode === "edit"
          ? {
              initialData: users?.[selectedIndex],
            }
          : {})}
        mode={mode}
      />
    </div>
  );
};

// Custom wrapper for BusinessTable specifically for UsersInformation
const UsersBusinessTable = ({
  columns,
  rows,
  renderCell,
  pagesCount,
  page,
  pageSize,
  handlePaginationChange,
  totalCount,
}: {
  columns: { type: string; value: string }[];
  rows: UserInfo[];
  renderCell: (
    column: string,
    row: UserInfo,
    rowIndex: number
  ) => React.ReactNode;
  pagesCount: number;
  page: number;
  pageSize: number;
  handlePaginationChange: (value: number) => void;
  totalCount: number;
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.usersTableWrapper}>
      <BusinessTable
        columns={columns}
        rows={rows}
        renderCell={(column, row, rowIndex) =>
          renderCell(column, row as UserInfo, rowIndex)
        }
      />
      <Pagination
        countName={t("lbl_user")}
        pagesCount={pagesCount}
        length={totalCount}
        page={page}
        pageSize={pageSize}
        handlePaginationChange={handlePaginationChange}
      />
    </div>
  );
};

export default UsersInformation;
